import { Box, Button, Icon, Input, InputGroup, InputLeftElement, Link } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { useFormik } from 'formik';
import { useState } from 'react';
import { MdEmail, MdLock, MdLockOutline } from 'react-icons/md';
import * as zod from 'zod';
import TogglePasswordVisibilityButton from '../../components/others/forms/set-password/TogglePasswordVisibilityButton';
import { IS_DEV } from '../../config';
import { LangKey } from '../../intl';
import { useDialogs } from '../../state/dialogs';
import { formValidate } from '../../state/forms/helpers.forms';
import { useIntl } from '../../state/intl';
import useSnacks from '../../utils/hooks/snacks.hooks';

interface LoginFormProps {
  loading: boolean;
  handleSubmit: (email: string, password: string) => void;
  handleResetPsw: (email: string) => Promise<void>;
}

const Form = styled.form`
  width: 400px;
  height: 100%;
  margin-top: 8px;
`;

const formSchema = zod.object({
  email: zod
    .string()
    .email()
    .nonempty(),
  password: zod.string().nonempty(),
});

const LoginForm: React.FC<LoginFormProps> = ({ handleSubmit, loading, handleResetPsw }) => {
  const { lang, txc } = useIntl();
  const { showWarning } = useSnacks();
  const { confirm } = useDialogs();
  const [showPassword, setShowPassword] = useState(false);

  type LoginFormValues = zod.TypeOf<typeof formSchema>;

  const form = useFormik<LoginFormValues>({
    validate: values => {
      if (IS_DEV && values.email === '' && values.password === '') {
        return {};
      }
      return formValidate(formSchema)(values);
    },
    initialValues: {
      email: '',
      password: '',
    },
    onSubmit: values => handleSubmit(values.email, values.password),
  });

  const { values, errors } = form;

  async function onResetPsw() {
    try {
      const email = formSchema.shape.email.parse(values.email);
      confirm({
        title: 'Reset password',
        content: tx.resetConfirm + ' ' + txc.doYouWantToProceed,
        onMainAction: () => handleResetPsw(email),
      });
    } catch (_) {
      showWarning('Please provide a valid email to request a password reset link');
      form.setFieldError('email', 'Please provide a valid email');
    }
  }

  const txs: {
    [key in LangKey]: {
      login: string;
      forgotPassword: string;
      resetConfirm: string;
    };
  } = {
    en: {
      login: 'Login',
      forgotPassword: 'Forgot password',
      resetConfirm: `An email with a reset password link will be sent to ${values.email}.`,
    },
    it: {
      login: 'Entra',
      forgotPassword: 'Ho dimenticato la password',
      resetConfirm: `Un link per eseguire il reset della password verrà inviato a ${values.email}.`,
    },
    es: {
      login: 'Login',
      forgotPassword: 'Forgot password',
      resetConfirm: `An email with a reset password link will be sent to ${values.email}.`,
    },
  };

  const tx = txs[lang];

  return (
    <Form onSubmit={form.handleSubmit}>
      <InputGroup>
        <InputLeftElement h="48px" pointerEvents="none">
          <Icon as={MdEmail} color="gray.300" />
        </InputLeftElement>

        <Input
          variant="outline"
          w="100%"
          h="48px"
          id="email"
          placeholder="Email"
          name="email"
          autoComplete="email"
          onChange={form.handleChange}
          onBlur={form.handleBlur}
          value={values.email}
          isInvalid={form.touched.email && Boolean(errors.email)}
          autoFocus
          bg="white"
          mb="15px"
          disabled={loading}
        />
      </InputGroup>

      <InputGroup>
        <InputLeftElement h="48px" pointerEvents="none">
          <Icon as={MdLock} color="gray.300" />
        </InputLeftElement>

        <Input
          variant="outline"
          w="100%"
          h="48px"
          name="password"
          placeholder="Password"
          type={showPassword ? 'text' : 'password'}
          id="password"
          bg="white"
          onChange={form.handleChange}
          onBlur={form.handleBlur}
          value={values.password}
          isInvalid={form.touched.password && Boolean(errors.password)}
          autoComplete="password"
          disabled={loading}
          pr={14}
        />

        <TogglePasswordVisibilityButton
          visible={showPassword}
          onToggle={() => setShowPassword(v => !v)}
        />
      </InputGroup>

      <Box ml={1} mt={2.5}>
        <Link fontSize={15} onClick={onResetPsw} color="gray.400" href="#">
          {tx.forgotPassword}
        </Link>
      </Box>

      <Button
        mt={8}
        type="submit"
        w="100%"
        colorScheme="primary"
        h="44px"
        leftIcon={<MdLockOutline style={{ fontSize: 16 }} />}
        isLoading={loading}
      >
        {tx.login}
      </Button>
    </Form>
  );
};

export default LoginForm;
